import axios, { AxiosRequestConfig } from 'axios';
import envConfig from '../configs/env';
import { ServiceNameEnum } from '../enums/serviceName';
import { AxiosRequestEnum } from '../enums/axiosRequest';

const defaultTimeout = 60000;

const axiosClient = axios.create();

axiosClient.defaults.baseURL = envConfig.axios.baseURL;

axiosClient.defaults.headers.common = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

// All request will wait 60 seconds before timeout
axiosClient.defaults.timeout = defaultTimeout;

axiosClient.interceptors.response.use(
  (response) => ({ data: response.data, error: null }),
  (error) => ({ data: null, error }),
);

type RequestParamsNoPayload = {
  path: string | null;
  accessToken?: string;
  requireAuth?: boolean;
  timeout?: number;
  endpoint?: EndpointT;
};
type RequestParamsWithPayload = {
  path: string | null;
  payload: any;
  accessToken?: string;
  requireAuth?: boolean;
  timeout?: number;
  endpoint?: EndpointT;
};

export type ErrorT = {
  code: string;
  message: string;
};

export type EndpointT = typeof AxiosRequestEnum.endpoint.backofficeApi
  | typeof AxiosRequestEnum.endpoint.coreApi;

const getRequestHeader = (
  token: string | undefined,
  timeout?: number | undefined,
  endpoint: EndpointT | undefined
  = AxiosRequestEnum.endpoint.backofficeApi,
): AxiosRequestConfig => {
  const config: AxiosRequestConfig = {};
  if (token) {
    config.headers = {
      Authorization: token ? `Bearer ${token}` : '',
    };
  }

  if (endpoint === AxiosRequestEnum.endpoint.coreApi) {
    config.baseURL = envConfig.axios.coreApiUrl;
  }

  if (timeout) {
    config.timeout = timeout;
  }

  return config;
};

const validateParams = (
  params: RequestParamsNoPayload | RequestParamsWithPayload,
): void => {
  if (!params.path) {
    throw new Error('Request path not found');
  }
};

export namespace AxiosService {
  export const getRefreshToken = async (
    refreshToken: string,
  ): Promise<string> => {
    const { data } = await axiosClient.post(
      ServiceNameEnum.auth.refreshToken,
      {
        refreshToken,
      },
      getRequestHeader(refreshToken),
    );
    return data?.accessToken || undefined;
  };

  export const getRequest = async (
    params: RequestParamsNoPayload,
  ): Promise<any> => {
    validateParams(params);
    return axiosClient.get(
      params.path!,
      getRequestHeader(params.accessToken, params.timeout, params.endpoint),
    );
  };

  export const postRequest = async (
    params: RequestParamsWithPayload,
  ): Promise<any> => {
    validateParams(params);
    return axiosClient.post(
      params.path!,
      params.payload,
      getRequestHeader(params.accessToken, params.timeout, params.endpoint),
    );
  };

  export const patchRequest = async (
    params: RequestParamsWithPayload,
  ): Promise<any> => {
    validateParams(params);
    return axiosClient.patch(
      params.path!,
      params.payload,
      getRequestHeader(params.accessToken, params.timeout, params.endpoint),
    );
  };

  export const putRequest = async (
    params: RequestParamsWithPayload,
  ): Promise<any> => {
    validateParams(params);
    return axiosClient.put(
      params.path!,
      params.payload,
      getRequestHeader(params.accessToken, params.timeout, params.endpoint),
    );
  };

  export const deleteRequest = async (
    params: RequestParamsNoPayload,
  ): Promise<any> => {
    validateParams(params);
    return axiosClient.delete(
      params.path!,
      getRequestHeader(params.accessToken, params.timeout, params.endpoint),
    );
  };

  export const getErrorResponse = (error: any): ErrorT | null =>
    error?.response?.data?.error?.errors?.[0];
}

export default axiosClient;
